import { useState, useEffect, useRef } from 'react'
import { Pipette } from 'lucide-react'
import type { ColorInputProps } from '../../types/contrast'
import { parseColor, rgbToHex } from '../../utils/contrastUtils'

export default function ColorInput({ label, color, onChange, id }: ColorInputProps) {
  const [hexInput, setHexInput] = useState(color.hex)
  const [showRgb, setShowRgb] = useState(false)
  const colorPickerRef = useRef<HTMLInputElement>(null)

  // Sync hex input when color prop changes externally
  useEffect(() => {
    if (color.isValid) {
      setHexInput(color.hex)
    }
  }, [color.hex, color.isValid])

  const handleHexChange = (value: string) => {
    setHexInput(value)
    // Auto-add # if user starts typing without it
    const normalizedValue = value.startsWith('#') ? value : `#${value}`
    const parsed = parseColor(normalizedValue)
    if (parsed.isValid) {
      onChange(parsed)
    } else {
      onChange({ ...color, hex: value, isValid: false })
    }
  }

  const handleRgbChange = (channel: 'r' | 'g' | 'b', value: string) => {
    const numValue = parseInt(value, 10)
    if (isNaN(numValue)) return

    const clampedValue = Math.max(0, Math.min(255, numValue))
    const newRgb = { ...color.rgb, [channel]: clampedValue }
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b)

    onChange({
      hex: newHex,
      rgb: newRgb,
      isValid: true
    })
  }

  const handleColorPickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseColor(e.target.value)
    if (parsed.isValid) {
      onChange(parsed)
    }
  }

  const openColorPicker = () => {
    colorPickerRef.current?.click()
  }

  return (
    <div className="space-y-3">
      <label htmlFor={`${id}-hex`} className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {/* HEX Input with Color Preview */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <div
            className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded border border-gray-300"
            style={{ backgroundColor: color.isValid ? color.hex : '#FFFFFF' }}
            aria-hidden="true"
          />
          <input
            id={`${id}-hex`}
            type="text"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
            placeholder="#000000"
            className={`w-full pl-12 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              color.isValid ? 'border-gray-300' : 'border-red-300 bg-red-50'
            }`}
            aria-describedby={!color.isValid ? `${id}-error` : undefined}
          />
        </div>

        {/* Native Color Picker Button */}
        <button
          type="button"
          onClick={openColorPicker}
          className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          aria-label="Open color picker"
        >
          <Pipette className="w-5 h-5 text-gray-600" />
        </button>
        <input
          ref={colorPickerRef}
          type="color"
          value={color.isValid ? color.hex : '#000000'}
          onChange={handleColorPickerChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {/* Error Message */}
      {!color.isValid && (
        <p id={`${id}-error`} className="text-sm text-red-600">
          Invalid color format. Use HEX (e.g., #FF5733) or RGB (e.g., 255, 87, 51)
        </p>
      )}

      {/* RGB Toggle */}
      <button
        type="button"
        onClick={() => setShowRgb(!showRgb)}
        className="text-sm text-purple-600 hover:text-purple-700 hover:underline focus:outline-none focus:underline"
      >
        {showRgb ? 'Hide RGB values' : 'Show RGB values'}
      </button>

      {/* RGB Inputs */}
      {showRgb && (
        <div className="flex items-center space-x-2">
          <div className="flex-1">
            <label htmlFor={`${id}-r`} className="block text-xs text-gray-500 mb-1">
              R
            </label>
            <input
              id={`${id}-r`}
              type="number"
              min="0"
              max="255"
              value={color.rgb.r}
              onChange={(e) => handleRgbChange('r', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label htmlFor={`${id}-g`} className="block text-xs text-gray-500 mb-1">
              G
            </label>
            <input
              id={`${id}-g`}
              type="number"
              min="0"
              max="255"
              value={color.rgb.g}
              onChange={(e) => handleRgbChange('g', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label htmlFor={`${id}-b`} className="block text-xs text-gray-500 mb-1">
              B
            </label>
            <input
              id={`${id}-b`}
              type="number"
              min="0"
              max="255"
              value={color.rgb.b}
              onChange={(e) => handleRgbChange('b', e.target.value)}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  )
}
